import { Button, Grid, TextField } from "@mui/material";
import { Box } from "@mui/system";
import { useFormik } from "formik";
import * as Yup from "yup";
import AdapterDateFns from "@date-io/date-fns";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { MobileDateTimePicker } from "@mui/x-date-pickers";
import useAuth from "hooks/useAuth";
import SuccessModal from "views/Components/SuccessModal";
import React, { useContext, useEffect } from "react";
import ErrorModal from "views/Components/ErrorModal";

import { AppGlobalContext } from "contexts/GlobalContext";
import Auth0Context from "contexts/Auth0Context";

// import dayjs from 'dayjs';

const ItemSchema = Yup.object().shape({
  publicaciones_sku: Yup.string().required("SKU es requerido"),
  ventas_num_de_venta: Yup.string().required("Num de venta es requerido"),
  envios_numero_de_seguimiento: Yup.string().required(
    "Numero de seguimiento es requerido",
  ),
  devoluciones_fecha_entregado: Yup.date().required(
    "Fecha entregado es requerido",
  ),
});

const baseUrl = process.env.REACT_APP_BASE_URL;

export default function AddItemForm() {
  const { user } = useContext(Auth0Context);
  const { mainCountry } = useContext(AppGlobalContext);
  const [disableButton, setDisableButton] = React.useState(false);
  const { accessToken } = useAuth();
  const [successOpen, setSuccessOpen] = React.useState(false);
  const [errorOpen, setErrorOpen] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState(null);
  const [country, setCountry] = React.useState(mainCountry);

  useEffect(() => {
    setCountry(mainCountry);
    cleanForm();
  }, [mainCountry]);

  const handleSuccessClose = () => {
    setSuccessOpen(false);
  };

  const handleErrorClose = () => {
    setErrorOpen(false);
  };

  const formik = useFormik({
    initialValues: {
      publicaciones_sku: "",
      ventas_num_de_venta: "",
      envios_numero_de_seguimiento: "",
      devoluciones_fecha_entregado: new Date(),
      pais: country.id,
    },
    validationSchema: ItemSchema,
    onSubmit: async (values) => {
      try {
        setDisableButton(true);
        const payload = {
          nroventa: values.ventas_num_de_venta,
          sku: values.publicaciones_sku,
          seguimiento: values.envios_numero_de_seguimiento,
          fecha: `${values.devoluciones_fecha_entregado.toLocaleDateString("en-US")} ${values.devoluciones_fecha_entregado.toLocaleTimeString(
            "en-US",
            { hour12: false },
          )}`,
          pais: country.id.toString(),
          userNickname: user.nickname,
        };

        const response = await fetch(
          `${baseUrl}/ServiceOperation/AddProductForm`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify(payload),
          },
        );

        if (response.ok) {
          setDisableButton(false);
          setSuccessOpen(true);
          formik.resetForm();
          return;
        }
        if (response.status === 409) {
          setDisableButton(false);
          setErrorOpen(true);
          setErrorMessage("Ya existe un registro con estos datos");
          return;
        }

        setDisableButton(false);
        setErrorOpen(true);
        setErrorMessage("Error al crear la devolución");
        return;
      } catch (error) {
        setDisableButton(false);
        console.error("Error", error);
        setErrorOpen(true);
        setErrorMessage("Error al crear la devolución");
      }
    },
  });

  const cleanForm = () => {
    formik.resetForm({
      values: {
        envios_numero_de_seguimiento: "",
        publicaciones_sku: "",
        ventas_num_de_venta: "",
      },
    });
  };

  const autocompleteFormShipping = async (seguimiento = null) => {
    try {
      const payload = {
        nroventa: "",
        fecha: "",
        sku: "",
        seguimiento: seguimiento,
        pais: country.id.toString(),
      };

      const request = await fetch(
        `${baseUrl}/ServiceOperation/ProductFormAutoComplete`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(payload),
        },
      );
      const response = await request.json();
      formik.setFieldValue(
        "publicaciones_sku",
        response?.sku || formik.values.publicaciones_sku || "",
      );
      formik.setFieldValue(
        "ventas_num_de_venta",
        response?.orderId || formik.values.ventas_num_de_venta || "",
      );
      formik.setFieldValue("devoluciones_fecha_entregado", new Date());
    } catch (error) {}
  };

  const autocompleteFormOrder = async (nroventa = null) => {
    try {
      const payload = {
        nroventa: nroventa,
        fecha: "",
        sku: "",
        seguimiento: "",
        pais: country.id.toString(),
      };

      const request = await fetch(
        `${baseUrl}/ServiceOperation/ProductFormAutoComplete`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(payload),
        },
      );
      const response = await request.json();
      formik.setFieldValue(
        "publicaciones_sku",
        response?.sku || formik.values.publicaciones_sku || "",
      );
      formik.setFieldValue(
        "envios_numero_de_seguimiento",
        response?.shipId || formik.values.envios_numero_de_seguimiento || "",
      );
      formik.setFieldValue("devoluciones_fecha_entregado", new Date());
    } catch (error) {}
  };

  const isDisabled = () => {
    return (
      formik.values.ventas_num_de_venta === "" ||
      formik.values.envios_numero_de_seguimiento === "" ||
      formik.values.publicaciones_sku === "" ||
      disableButton
    );
  };

  return (
    <Box sx={{ mt: 2 }}>
      <SuccessModal
        handleClose={handleSuccessClose}
        open={successOpen}
        message="Success"
        message2="Se creo la devolución correctamente!"
      />
      <ErrorModal
        handleClose={handleErrorClose}
        open={errorOpen}
        message="Error"
        message2={errorMessage}
      />
      <form onSubmit={formik.handleSubmit} noValidate>
        <Grid
          container
          spacing={{ xs: 2, md: 3 }}
          columns={{ xs: 4, sm: 8, md: 12 }}
        >
          <Grid item xs={12} md={6}>
            <TextField
              id="ventas_num_de_venta"
              label="Número de Venta"
              sx={{
                width: "100%",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderRadius: "5px",

                  border: "1px solid #0000003B",
                },
                "& .MuiInputBase-input": {
                  backgroundColor: "#FFFFFF",
                },
                "& .MuiInputBase-root": {
                  backgroundColor: "#FFFFFF",
                },
              }}
              value={formik.values.ventas_num_de_venta}
              required
              onChange={(ev) => {
                formik.handleChange(ev);
                autocompleteFormOrder(ev.target.value);
              }}
              error={
                formik.touched.ventas_num_de_venta &&
                Boolean(formik.errors.ventas_num_de_venta)
              }
              helperText={
                formik.touched.ventas_num_de_venta &&
                formik.errors.ventas_num_de_venta
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              sx={{
                width: "100%",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderRadius: "5px",

                  border: "1px solid #0000003B",
                },
                "& .MuiInputBase-input": {
                  backgroundColor: "#FFFFFF",
                },
                "& .MuiInputBase-root": {
                  backgroundColor: "#FFFFFF",
                },
              }}
              id="envios_numero_de_seguimiento"
              label="Número de Seguimiento"
              value={formik.values.envios_numero_de_seguimiento}
              required
              onChange={(ev) => {
                formik.handleChange(ev);
                autocompleteFormShipping(ev.target.value);
              }}
              error={
                formik.touched.envios_numero_de_seguimiento &&
                Boolean(formik.errors.envios_numero_de_seguimiento)
              }
              helperText={
                formik.touched.envios_numero_de_seguimiento &&
                formik.errors.envios_numero_de_seguimiento
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              sx={{
                width: "100%",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderRadius: "5px",

                  border: "1px solid #0000003B",
                },
                "& .MuiInputBase-input": {
                  backgroundColor: "#FFFFFF",
                },
                "& .MuiInputBase-root": {
                  backgroundColor: "#FFFFFF",
                },
              }}
              id="publicaciones_sku"
              label="SKU"
              value={formik.values.publicaciones_sku}
              required
              onChange={formik.handleChange}
              error={
                formik.touched.publicaciones_sku &&
                Boolean(formik.errors.publicaciones_sku)
              }
              helperText={
                formik.touched.publicaciones_sku &&
                formik.errors.publicaciones_sku
              }
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <MobileDateTimePicker
                label="Fecha de entrega"
                value={formik.values.devoluciones_fecha_entregado}
                onChange={(newValue) => {
                  formik.setFieldValue(
                    "devoluciones_fecha_entregado",
                    newValue,
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    sx={{
                      width: "100%",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderRadius: "5px",

                        border: "1px solid #0000003B",
                      },
                      "& .MuiInputBase-input": {
                        backgroundColor: "#FFFFFF",
                      },
                      "& .MuiInputBase-root": {
                        backgroundColor: "#FFFFFF",
                      },
                    }}
                    {...params}
                  />
                )}
              />
            </LocalizationProvider>
          </Grid>
        </Grid>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            mt: 2,
            gap: 2,
          }}
        >
          <Button
            type="button"
            variant="outlined"
            onClick={cleanForm}
            style={{
              color: "#002878",
              fontWeight: 500,
              borderColor: "#002878",
              "&:hover": { borderColor: "#002878" },
              width: "120px",
              textTransform: "uppercase",
            }}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            style={{
              color: isDisabled() ? "rgba(0, 0, 0, 0.26)" : "white",
              backgroundColor: isDisabled() ? "#0000001F" : "#002878",
              "&:hover": {
                backgroundColor: isDisabled() ? "#0000001F" : "#002878",
              },
              width: "120px",
              textTransform: "uppercase",
            }}
            disabled={
              disableButton ||
              Object.values(formik.values).some((value) => value === "")
            }
          >
            Ingresar
          </Button>
        </Box>
      </form>
    </Box>
  );
}
